import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { AsyncLoadStatus } from '../../../common/types';
import TitleSection from '../../../components/layout/TitleSection';
import {
  CORE_FEATURES,
  featureToggleState,
  menuItemsState,
  menuItemVisibilityState,
  useInitializeMenuItems,
} from '../../../store/menu.state';
import { MenuItem, MenuVisibility } from '../../../store/menu.types';
import {
  warehouseSelected,
  warehouseUpdateLoadStatus,
} from '../../../store/warehouse.state';
import useMenuFeatureSettings from '../../../warehouse/hooks/useMenuFeatureSettings';

// Constants for localStorage keys
const STORAGE_KEY_FEATURE_TOGGLES = 'warebee_feature_toggles';
const STORAGE_KEY_ITEM_VISIBILITY = 'warebee_item_visibility';
const STORAGE_KEY_MENU_ORDER = 'warebee_menu_order';

const MenuFeatureConfigurator: React.FC = () => {
  const { t } = useTranslation('app');
  const warehouse = useRecoilValue(warehouseSelected);
  const status = useRecoilValue(warehouseUpdateLoadStatus);
  const menuItems = useRecoilValue(menuItemsState);
  const initialMenuItems = useInitializeMenuItems();
  const [featureToggles, setFeatureToggles] =
    useRecoilState(featureToggleState);
  const [itemVisibility, setItemVisibility] = useRecoilState(
    menuItemVisibilityState,
  );
  const setMenuItems = useSetRecoilState(menuItemsState);
  const { isFeatureEnabled, updateFeatureSettings, saveMenuOrder } =
    useMenuFeatureSettings();

  // State for menu order editing
  const [reordering, setReordering] = useState(false);
  const [editedMenuItems, setEditedMenuItems] = useState<MenuItem[]>([]);

  // Load saved state from localStorage on initial render
  useEffect(() => {
    if (menuItems.length === 0) return;

    try {
      // Load feature toggles from localStorage
      const savedFeatureToggles = localStorage.getItem(
        STORAGE_KEY_FEATURE_TOGGLES,
      );
      if (savedFeatureToggles) {
        const parsedFeatureToggles = JSON.parse(savedFeatureToggles);
        setFeatureToggles(prev => ({
          ...prev,
          ...parsedFeatureToggles,
        }));
      }

      // Load item visibility from localStorage
      const savedItemVisibility = localStorage.getItem(
        STORAGE_KEY_ITEM_VISIBILITY,
      );
      if (savedItemVisibility) {
        const parsedItemVisibility = JSON.parse(savedItemVisibility);
        setItemVisibility(prev => ({
          ...prev,
          ...parsedItemVisibility,
        }));
      }

      // Load menu order from localStorage
      const savedMenuOrder = localStorage.getItem(STORAGE_KEY_MENU_ORDER);
      if (savedMenuOrder) {
        const parsedMenuOrder = JSON.parse(savedMenuOrder);

        // Update menu items with saved order
        if (Array.isArray(parsedMenuOrder) && parsedMenuOrder.length > 0) {
          const updatedMenuItems = menuItems.map(item => {
            const orderItem = parsedMenuOrder.find(
              (orderItem: { id: string; order: number }) =>
                item.id === orderItem.id,
            );
            if (orderItem) {
              return { ...item, order: orderItem.order };
            }
            return item;
          });
          setMenuItems(updatedMenuItems);
        }
      }
    } catch (error) {
      console.error(
        'Error loading menu configuration from localStorage:',
        error,
      );
    }
  }, [menuItems.length, setFeatureToggles, setItemVisibility, setMenuItems]);

  // Update edited items when menu items change
  useEffect(() => {
    if (!reordering && menuItems.length > 0) {
      setEditedMenuItems([...menuItems]);
    }
  }, [menuItems, reordering]);

  // Initialize feature toggles - skip if already loaded from localStorage
  useEffect(() => {
    if (
      !localStorage.getItem(STORAGE_KEY_FEATURE_TOGGLES) &&
      menuItems.length > 0
    ) {
      setFeatureToggles(prev => {
        const updatedToggles = { ...prev };
        menuItems
          .filter(item => item.featureKey && !updatedToggles[item.featureKey])
          .forEach(item => {
            if (item.featureKey) {
              const isCore = CORE_FEATURES.includes(item.featureKey);
              const isDefault = item.default;
              updatedToggles[item.featureKey] = isCore || isDefault;
            }
          });
        return updatedToggles;
      });
    }
  }, [menuItems, setFeatureToggles]);

  // Initialize individual menu item visibility - skip if already loaded from localStorage
  useEffect(() => {
    if (
      !localStorage.getItem(STORAGE_KEY_ITEM_VISIBILITY) &&
      menuItems.length > 0
    ) {
      setItemVisibility(prev => {
        const updatedVisibility = { ...prev };
        menuItems.forEach(item => {
          if (updatedVisibility[item.id] === undefined) {
            updatedVisibility[item.id] = !!item.default;
          }
        });
        return updatedVisibility;
      });
    }
  }, [menuItems, setItemVisibility]);

  // Save feature toggles to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(featureToggles).length > 0) {
      localStorage.setItem(
        STORAGE_KEY_FEATURE_TOGGLES,
        JSON.stringify(featureToggles),
      );
    }
  }, [featureToggles]);

  // Save item visibility to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(itemVisibility).length > 0) {
      localStorage.setItem(
        STORAGE_KEY_ITEM_VISIBILITY,
        JSON.stringify(itemVisibility),
      );
    }
  }, [itemVisibility]);

  if (!warehouse) {
    return null; // or loading indicator
  }

  const isLoading = status === AsyncLoadStatus.Loading;
  const serviceOptions = [{ label: t`On` }, { label: t`Off` }];

  // Get all configurable menu items that have feature keys
  const allConfigurableMenuItems = menuItems.filter(item => item.featureKey);

  // Use all items with feature keys (fixed to show more toggles)
  const featureDependentItems = allConfigurableMenuItems;

  // Group menu items by feature key for easier management
  const menuItemsByFeature = featureDependentItems.reduce(
    (acc, item) => {
      const featureKey = item.featureKey!;
      if (!acc[featureKey]) {
        acc[featureKey] = [];
      }
      acc[featureKey].push(item);
      return acc;
    },
    {} as Record<string, typeof featureDependentItems>,
  );

  // Get unique feature keys
  const featureKeys = Object.keys(menuItemsByFeature);

  // Check if a feature is enabled
  const isFeatureEnabledLocally = (featureKey: string): boolean => {
    return featureToggles?.[featureKey] !== false; // Default to true if not set
  };

  // Check if a menu item is visible
  const isMenuItemVisible = (itemId: string): boolean => {
    return itemVisibility?.[itemId] !== false; // Default to true if not set
  };

  // Update feature settings globally
  const updateFeatureSettingsLocally = (
    featureKey: string,
    enabled: boolean,
  ) => {
    if (!featureKey) return;

    // Update the recoil state for toggles
    setFeatureToggles(prev => {
      // Only update if the value is different to prevent unnecessary re-renders
      if (prev[featureKey] === enabled) return prev;
      return {
        ...prev,
        [featureKey]: enabled,
      };
    });

    // Also call the original function (which is now a no-op)
    updateFeatureSettings({
      featureKey,
      enabled,
    });
  };

  // Update menu visibility
  const updateMenuVisibility = (
    itemId: string,
    newVisibility: MenuVisibility,
  ) => {
    if (!itemId) return;

    const updatedMenuItems = menuItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          visibility: newVisibility,
        };
      }
      return item;
    });

    setMenuItems(updatedMenuItems);

    // Save the updated menu order to localStorage
    const orderData = updatedMenuItems.map(item => ({
      id: item.id,
      order: item.order,
      visibility: item.visibility,
    }));
    localStorage.setItem(STORAGE_KEY_MENU_ORDER, JSON.stringify(orderData));
  };

  // Update individual menu item visibility
  const updateMenuItemVisibility = (itemId: string, visible: boolean) => {
    if (!itemId) return;

    // Update the recoil state for item visibility
    setItemVisibility(prev => {
      // Only update if the value is different to prevent unnecessary re-renders
      if (prev[itemId] === visible) return prev;
      return {
        ...prev,
        [itemId]: visible,
      };
    });
  };

  // Start reordering menu items
  const startReordering = () => {
    setReordering(true);
    setEditedMenuItems([...menuItems]);
  };

  // Save reordered menu items
  const handleSaveReordering = () => {
    saveMenuOrder(editedMenuItems);
    setMenuItems(editedMenuItems);
    setReordering(false);

    // Save the updated menu order to localStorage
    const orderData = editedMenuItems.map(item => ({
      id: item.id,
      order: item.order,
    }));
    localStorage.setItem(STORAGE_KEY_MENU_ORDER, JSON.stringify(orderData));
  };

  // Cancel reordering
  const cancelReordering = () => {
    setReordering(false);
    setEditedMenuItems([...menuItems]);
  };

  // Move menu item up in order
  const moveItemUp = (id: string) => {
    const itemIndex = editedMenuItems.findIndex(item => item.id === id);
    if (itemIndex <= 0) return; // Can't move first item up

    const newItems = editedMenuItems.map(item => ({ ...item })); // Create deep copies
    const item = newItems[itemIndex];
    const prevItem = newItems[itemIndex - 1];

    // Swap orders
    const tempOrder = item.order;
    item.order = prevItem.order;
    prevItem.order = tempOrder;

    // Create a new sorted array without mutating the original
    const sortedItems = [...newItems].sort((a, b) => a.order - b.order);

    setEditedMenuItems(sortedItems);
  };

  // Move menu item down in order
  const moveItemDown = (id: string) => {
    const itemIndex = editedMenuItems.findIndex(item => item.id === id);
    if (itemIndex >= editedMenuItems.length - 1) return; // Can't move last item down

    const newItems = editedMenuItems.map(item => ({ ...item })); // Create deep copies
    const item = newItems[itemIndex];
    const nextItem = newItems[itemIndex + 1];

    // Swap orders
    const tempOrder = item.order;
    item.order = nextItem.order;
    nextItem.order = tempOrder;

    // Create a new sorted array without mutating the original
    const sortedItems = [...newItems].sort((a, b) => a.order - b.order);

    setEditedMenuItems(sortedItems);
  };

  // Reset to defaults function
  const resetToDefaults = () => {
    // Get the default menu configuration
    const defaultFeatureToggles: Record<string, boolean> = {};

    // Always enable core features
    CORE_FEATURES.forEach(feature => {
      defaultFeatureToggles[feature] = true;
    });

    // Set additional defaults based on menu definition
    initialMenuItems.forEach(item => {
      if (item.featureKey) {
        // Only enable if specified as default in the menu configuration
        defaultFeatureToggles[item.featureKey] = !!item.default;
      }
    });

    // Reset feature toggles to default values
    setFeatureToggles(defaultFeatureToggles);

    // Reset menu item visibility based on the 'default' property
    const defaultItemVisibility: Record<string, boolean> = {};
    initialMenuItems.forEach(item => {
      // Set visibility based on the 'default' property from menu configuration
      defaultItemVisibility[item.id] = !!item.default;
    });

    setItemVisibility(defaultItemVisibility);

    // Reset menu items to their default order
    const defaultOrderedItems = [...initialMenuItems].sort(
      (a, b) => a.order - b.order,
    );
    setMenuItems(defaultOrderedItems);

    // Reset to default menu items if in reordering mode
    if (reordering) {
      const sortedDefaultItems = [...initialMenuItems].sort(
        (a, b) => a.order - b.order,
      );
      setEditedMenuItems(sortedDefaultItems);
    }

    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY_FEATURE_TOGGLES);
    localStorage.removeItem(STORAGE_KEY_ITEM_VISIBILITY);
    localStorage.removeItem(STORAGE_KEY_MENU_ORDER);

    console.log('Menu configuration reset to defaults', defaultFeatureToggles);
  };

  return (
    <div className="space-y-6">
      {menuItems.length > 0 && (
        <TitleSection inPanelView title={t`Menu Configuration`}>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-menu-text/70 text-sm">
              Configure menu visibility and order
            </div>
            <button
              onClick={resetToDefaults}
              className="bg-menu rounded-md px-3 py-1 text-sm text-white"
              disabled={isLoading}
            >
              {t`Reset to Default`}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="divide-menu/20 min-w-full divide-y">
              <thead className="bg-app-panel-dark/50">
                <tr>
                  <th
                    scope="col"
                    className="text-menu-text/70 px-4 py-2 text-left text-xs uppercase tracking-wider"
                  >
                    Menu Item
                  </th>
                  <th
                    scope="col"
                    className="text-menu-text/70 px-4 py-2 text-center text-xs uppercase tracking-wider"
                  >
                    Sidebar
                  </th>
                  <th
                    scope="col"
                    className="text-menu-text/70 px-4 py-2 text-center text-xs uppercase tracking-wider"
                  >
                    Header
                  </th>
                  <th
                    scope="col"
                    className="text-menu-text/70 px-4 py-2 text-center text-xs uppercase tracking-wider"
                  >
                    Order
                  </th>
                </tr>
              </thead>
              <tbody className="bg-app-panel-dark/20 divide-menu/10 divide-y">
                {[...menuItems]
                  .sort((a, b) => a.order - b.order)
                  .map(item => {
                    const isCore = item.featureKey
                      ? CORE_FEATURES.includes(item.featureKey)
                      : false;
                    const isEnabled =
                      featureToggles[item.featureKey || ''] !== false;
                    const isVisible = itemVisibility[item.id] !== false;
                    const isSidebar = [
                      MenuVisibility.SIDEBAR,
                      MenuVisibility.BOTH,
                    ].includes(item.visibility as MenuVisibility);
                    const isHeader = [
                      MenuVisibility.HEADER,
                      MenuVisibility.BOTH,
                    ].includes(item.visibility as MenuVisibility);

                    // Determine if the checkboxes should be checked based on actual visibility
                    const showInSidebar =
                      isSidebar && (isCore || (isEnabled && isVisible));
                    const showInHeader =
                      isHeader && (isCore || (isEnabled && isVisible));

                    return (
                      <tr
                        key={item.id}
                        className={classNames(
                          item.disabled ? 'opacity-50' : '',
                          'hover:bg-menu/5',
                        )}
                      >
                        <td className="whitespace-nowrap px-4 py-2">
                          <div className="flex items-center">
                            <div className="text-menu-text text-sm font-medium">
                              {item.title}
                              {item.beta && (
                                <span className="text-minimal bg-menu-active text-menu-active-text mx-1 rounded-sm px-2 py-1">
                                  BETA
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-menu-text/50 text-xs">
                            {item.subtitle}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <div className="inline-flex">
                            <button
                              className={classNames(
                                'h-5 w-5 rounded',
                                'flex items-center justify-center',
                                showInSidebar
                                  ? 'bg-menu-active'
                                  : 'bg-menu border-menu/20',
                              )}
                              onClick={() => {
                                if (isCore) return; // Don't allow toggling core features
                                const newVisibility = showInSidebar
                                  ? showInHeader
                                    ? MenuVisibility.HEADER
                                    : MenuVisibility.NONE
                                  : showInHeader
                                    ? MenuVisibility.BOTH
                                    : MenuVisibility.SIDEBAR;
                                updateMenuVisibility(item.id, newVisibility);
                                updateMenuItemVisibility(item.id, true);
                                if (item.featureKey) {
                                  updateFeatureSettingsLocally(
                                    item.featureKey,
                                    true,
                                  );
                                }
                              }}
                              disabled={isCore} // Disable button for core features
                            >
                              {showInSidebar && (
                                <span className="text-menu-active-text text-xs">
                                  ✓
                                </span>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <div className="inline-flex">
                            <button
                              className={classNames(
                                'h-5 w-5 rounded',
                                'flex items-center justify-center',
                                showInHeader
                                  ? 'bg-menu-active'
                                  : 'bg-menu border-menu/20',
                              )}
                              onClick={() => {
                                if (isCore) return; // Don't allow toggling core features
                                const newVisibility = showInHeader
                                  ? showInSidebar
                                    ? MenuVisibility.SIDEBAR
                                    : MenuVisibility.NONE
                                  : showInSidebar
                                    ? MenuVisibility.BOTH
                                    : MenuVisibility.HEADER;
                                updateMenuVisibility(item.id, newVisibility);
                                updateMenuItemVisibility(item.id, true);
                                if (item.featureKey) {
                                  updateFeatureSettingsLocally(
                                    item.featureKey,
                                    true,
                                  );
                                }
                              }}
                              disabled={isCore} // Disable button for core features
                            >
                              {showInHeader && (
                                <span className="text-menu-active-text text-xs">
                                  ✓
                                </span>
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => moveItemUp(item.id)}
                              className="text-menu-text/70 hover:bg-menu/10 rounded p-1"
                              title={t`Move Up`}
                              disabled={menuItems.indexOf(item) === 0}
                            >
                              <span className="text-xs">↑</span>
                            </button>
                            <span className="text-menu-text/80 text-xs">
                              {item.order}
                            </span>
                            <button
                              onClick={() => moveItemDown(item.id)}
                              className="text-menu-text/70 hover:bg-menu/10 rounded p-1"
                              title={t`Move Down`}
                              disabled={
                                menuItems.indexOf(item) === menuItems.length - 1
                              }
                            >
                              <span className="text-xs">↓</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </TitleSection>
      )}
    </div>
  );
};

export default MenuFeatureConfigurator;
